import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { useAppDispatch } from '@store/hooks'
import { updateInternalConversation } from '@store/actions/chat-internal.action'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'

type InternalChatChangeNameModalProps = {
  show: boolean
  onHide: () => void
  initialValue: string
  conversationId: string
}

const INTERNAL_CHAT_CHANGE_NAME_LOADING_CONTAINER =
  '.internal-chat-change-name-modal .modal-content'

const InternalChatChangeNameModal: React.FC<
  InternalChatChangeNameModalProps
> = ({ show, onHide, initialValue, conversationId }): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [groupChatNameInput, setGroupChatNameInput] = useState<string>('')

  useEffect(() => {
    if (show) {
      setGroupChatNameInput(initialValue)
    }
  }, [show, initialValue])

  const handleSave = async (): Promise<void> => {
    const newName = groupChatNameInput.trim()
    if (!newName || newName === initialValue.trim()) {
      onHide()
      return
    }

    try {
      showLoading({ container: INTERNAL_CHAT_CHANGE_NAME_LOADING_CONTAINER })
      await dispatch(
        updateInternalConversation({
          id: conversationId,
          data: { name: newName },
        }),
      ).unwrap()
      showToast(
        createSuccessToast(
          'Name updated',
          'Group chat name has been updated successfully.',
        ),
      )
      onHide()
    } catch {
      showToast(
        createFailedToast(
          'Update failed',
          'Failed to update group chat name. Please try again.',
        ),
      )
    } finally {
      hideLoading(INTERNAL_CHAT_CHANGE_NAME_LOADING_CONTAINER)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="internal-chat-change-name-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Change group chat name</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="internal-chat-change-name-input">
          <input
            type="text"
            className="form-control"
            placeholder="Enter group chat name"
            value={groupChatNameInput}
            onChange={(e) => setGroupChatNameInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button size="md" className="button-secondary me-2" onClick={onHide}>
          Cancel
        </Button>
        <Button
          size="md"
          className="button-primary"
          onClick={handleSave}
          disabled={
            !groupChatNameInput.trim() ||
            initialValue === groupChatNameInput.trim()
          }
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default InternalChatChangeNameModal
